import { useEffect, useState } from "react";
import { Flame, Zap, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * EventBanner — thin top-of-dashboard banner announcing active LiveOps events
 * (Ember Rush 2× gold weekend, Ashen Trial, etc.) and Season Pass status.
 * Auto-hides if nothing is active. Dismissable per session (sessionStorage).
 */
export default function EventBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("event_banner_dismissed") === "1") {
        setDismissed(true);
      }
    } catch { /* ignore */ }
    let cancelled = false;
    api.get("/events/active")
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { /* no auth / no event → nothing to render */ });
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !data) return null;
  const { event, season_pass_active } = data;
  if (!event && !season_pass_active) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("event_banner_dismissed", "1"); } catch { /* ignore */ }
  };

  return (
    <div
      className="panel mb-4 px-4 py-2 flex items-center gap-3 relative overflow-hidden"
      data-testid="event-banner"
      style={{
        background: event
          ? "linear-gradient(90deg, rgba(60,20,10,0.7), rgba(140,60,20,0.4), rgba(60,20,10,0.7))"
          : "linear-gradient(90deg, rgba(30,25,10,0.7), rgba(90,70,20,0.35), rgba(30,25,10,0.7))",
        borderColor: event ? "rgba(226,88,34,0.55)" : "rgba(212,175,55,0.45)",
      }}
    >
      {event ? (
        <>
          <Flame size={16} className="ember" style={{ filter: "drop-shadow(0 0 6px #e25822)" }} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.4em] ember">LIVE · {event.name?.toUpperCase()}</div>
            <div className="font-narrative text-sm text-[#f0e6d2] italic truncate">{event.tagline}</div>
          </div>
        </>
      ) : (
        <>
          <Zap size={16} className="gold-text" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.4em] gold-text">SEASON PASS · ACTIVE</div>
            <div className="font-narrative text-sm text-[#f0e6d2] italic truncate">+2 daily embers, +10% XP, exclusive skin</div>
          </div>
        </>
      )}
      <button
        onClick={dismiss}
        className="text-[#8b7965] hover:text-[#f0e6d2] transition-colors"
        data-testid="event-banner-close"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
